import { useState } from "react";
import { Link } from "react-router-dom";
import { HelpCircle, User, AlertTriangle, FileText } from "lucide-react";
import { toast } from "sonner";
import SEOHead, { generateWebPageSchema } from "@/components/SEOHead";
import api from "@/lib/api";

const CATEGORIES = [
  { value: "", label: "Select a topic" },
  { value: "general", label: "General Question" },
  { value: "account", label: "Account Issue" },
  { value: "dispute", label: "Dispute or Complaint" },
  { value: "refund", label: "Refund Request" },
  { value: "account", label: "Account & Verification" },
  { value: "trust-safety", label: "Trust & Safety Report" },
  { value: "partnership", label: "Partnership or Press" },
  { value: "other", label: "Something Else" },
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", category: "", message: "" });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! We'll get back to you within 24-48 hours.");
      setForm({ name: "", email: "", category: "", message: "" });
    } catch {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <SEOHead
        title="Contact Us | Paradise Borrowed"
        description="Get help, report issues, or reach out to the Paradise Borrowed team. We typically respond within 24 hours."
        canonicalUrl="https://www.paradiseborrowed.com/contact"
        type="website"
        schema={generateWebPageSchema({ title: "Contact Us", description: "Paradise Borrowed support center", url: "https://www.paradiseborrowed.com/contact" })}
      />

      <div className="max-w-4xl mx-auto px-4 py-12" data-testid="contact-page">
        {/* Hero */}
        <div className="text-center pb-12 mb-12 border-b border-stone-200">
          <p className="text-xs tracking-widest uppercase text-stone-400 mb-4 font-ui">Support Center</p>
          <h1 className="text-4xl sm:text-5xl text-stone-900 mb-4" style={{ fontFamily: "'Fraunces', serif", fontWeight: 400 }}>
            How can we help<br /><span style={{ fontStyle: "italic", color: "#E8541A" }}>you today?</span>
          </h1>
          <p className="text-stone-500 max-w-xl mx-auto leading-relaxed mb-8 font-ui">
            Find answers, get support, or reach out directly. We're here to make your experience smooth.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <a href="#contact-form" className="px-6 py-2.5 rounded-lg border text-sm transition-all bg-coral text-white border-coral hover:bg-[#d44814] font-ui" data-testid="contact-cta-form">
              Contact Support
            </a>
            <Link to="/how-it-works" className="px-6 py-2.5 rounded-lg border text-sm transition-all bg-white text-stone-600 border-stone-300 hover:border-coral hover:text-coral font-ui">
              How It Works
            </Link>
          </div>
        </div>

        {/* Support Categories */}
        <div className="mb-12">
          <p className="text-xs tracking-widest uppercase text-stone-400 mb-6 font-ui">Browse by topic</p>

          <TopicCard
            icon={<HelpCircle className="w-5 h-5 text-coral" />}
            title="Getting Help"
            desc="Resources for understanding the platform and resolving common issues."
            links={[
              { href: "/how-it-works", label: "How It Works", sub: "Platform overview for borrowers and lenders" },
              { href: "/fees", label: "Fees & Pricing", sub: "Transparent breakdown of platform costs" },
              { href: "/trust", label: "Trust & Safety", sub: "How we keep the community safe" },
              { href: "/browse", label: "Browse Listings", sub: "Find items available near you" },
            ]}
          />

          <TopicCard
            icon={<User className="w-5 h-5 text-coral" />}
            title="Your Account"
            desc="Manage your profile, settings, and account security."
            links={[
              { href: "/messages", label: "Messages", sub: "Contact lenders or borrowers directly" },
              { href: "/dashboard", label: "Your Bookings", sub: "View past transactions and write reviews" },
              { href: "/profile", label: "Profile Settings", sub: "Update your profile and preferences" },
              { href: "/how-it-works", label: "Email Verification", sub: "Help with email verification issues" },
            ]}
          />

          <TopicCard
            icon={<AlertTriangle className="w-5 h-5 text-coral" />}
            title="Issues & Disputes"
            desc="Report problems, file disputes, or get help with transactions."
            links={[
              { href: "/trust", label: "Dispute Resolution", sub: "How disputes are handled and resolved" },
              { href: "/fees", label: "Refunds & Cancellations", sub: "Cancellation policy and refund timeline" },
              { href: "/trust", label: "Cancellation Policies", sub: "Flexible, Moderate, and Strict policy tiers" },
              { href: "/trust", label: "Trust & Safety", sub: "Report fraud or safety concerns" },
            ]}
          />

          <TopicCard
            icon={<FileText className="w-5 h-5 text-coral" />}
            title="Legal & Business"
            desc="Official policies, partnerships, and legal documentation."
            links={[
              { href: "/terms-of-service", label: "Terms of Service", sub: "User agreement and platform rules" },
              { href: "/privacy-policy", label: "Privacy Policy", sub: "How we handle your data" },
              { href: "/about", label: "About Us", sub: "Our story and mission" },
              { href: "/fees", label: "Regional Coverage", sub: "US states + Caribbean territories" },
            ]}
            last
          />
        </div>

        <div className="h-px bg-stone-200 my-12" />

        {/* Contact Form */}
        <div id="contact-form" className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <p className="text-xs tracking-widest uppercase text-stone-400 mb-3 font-ui">Still need help?</p>
            <h2 className="text-3xl text-stone-900 mb-3" style={{ fontFamily: "'Fraunces', serif", fontWeight: 400 }}>Get in touch</h2>
            <p className="text-stone-500 text-sm font-ui">We typically respond within 24 hours during business days.</p>
          </div>

          <div className="bg-stone-50 border border-stone-200 rounded-2xl p-8">
            <form onSubmit={handleSubmit} data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-medium text-stone-700 mb-2 font-ui">Name</label>
                  <input
                    type="text" required value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-4 py-2.5 text-sm border border-stone-300 rounded-lg focus:outline-none focus:border-coral transition-colors bg-white font-ui"
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-700 mb-2 font-ui">Email</label>
                  <input
                    type="email" required value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full px-4 py-2.5 text-sm border border-stone-300 rounded-lg focus:outline-none focus:border-coral transition-colors bg-white font-ui"
                    data-testid="contact-email"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-xs font-medium text-stone-700 mb-2 font-ui">What do you need help with?</label>
                <select
                  required value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full px-4 py-2.5 text-sm border border-stone-300 rounded-lg focus:outline-none focus:border-coral transition-colors bg-white font-ui"
                  data-testid="contact-category"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>

              <div className="mb-6">
                <label className="block text-xs font-medium text-stone-700 mb-2 font-ui">Message</label>
                <textarea
                  required rows="5" value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full px-4 py-2.5 text-sm border border-stone-300 rounded-lg focus:outline-none focus:border-coral transition-colors resize-none bg-white font-ui"
                  data-testid="contact-message"
                />
              </div>

              <button
                type="submit" disabled={sending}
                className="w-full py-3 bg-coral text-white rounded-lg font-medium text-sm hover:bg-[#d44814] transition-colors disabled:opacity-50 font-ui"
                data-testid="contact-submit"
              >
                {sending ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>

        {/* Footer Note */}
        <div className="text-center mt-12 pt-8 border-t border-stone-200">
          <p className="text-xs text-stone-400 leading-relaxed font-ui">
            ParadiseBorrowed.com is a Delaware corporation. Operating in select US states and Caribbean territories.{" "}
            <Link to="/terms-of-service" className="text-coral hover:underline">Terms</Link> ·{" "}
            <Link to="/privacy-policy" className="text-coral hover:underline">Privacy</Link> ·{" "}
            <Link to="/trust" className="text-coral hover:underline">Dispute Resolution</Link>
          </p>
        </div>
      </div>
    </>
  );
}

function TopicCard({ icon, title, desc, links, last }) {
  return (
    <div className={`bg-white border border-stone-200 rounded-2xl p-8 ${last ? "" : "mb-6"}`} data-testid={`topic-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start gap-4 mb-4">
        <div className="w-10 h-10 rounded-lg bg-coral-light flex items-center justify-center shrink-0">{icon}</div>
        <div className="flex-1">
          <h2 className="text-2xl text-stone-900 mb-2" style={{ fontFamily: "'Fraunces', serif", fontWeight: 400 }}>{title}</h2>
          <p className="text-stone-500 text-sm mb-4 font-ui">{desc}</p>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4 pl-14">
        {links.map((link) => (
          <Link to={link.href} key={link.label} className="group">
            <div className="text-sm font-medium text-stone-900 group-hover:text-coral transition-colors mb-1 font-ui">{link.label}</div>
            <div className="text-xs text-stone-500 font-ui">{link.sub}</div>
          </Link>
        ))}
      </div>
    </div>
  );
}
